/**
 * Pin-first-product (Den Liu / WeScale "Pin the Clicked Product" approach).
 *
 * When an ad deep-links to a collection page with `?first=<product-handle>`,
 * the clicked product is shown as the FIRST card in the grid - replicating
 * Facebook Shop's behavior where the tapped catalog item is always in the #1
 * position. For Meta catalog ads, enable "Override catalog website deep links"
 * and template the URL as:
 *   https://tinygainsclub.com/collections/sale?first={{product.handle}}
 *
 * Two paths:
 * 1. Product is already in the server-rendered grid -> move its <li> to the
 *    front. Perfect fidelity, no network request.
 * 2. Product is on a later page (common: Sale has 370+ products, 24/page) ->
 *    fetch `/products/<handle>.js`, clone the grid's first card as a styling
 *    template, and swap in the pinned product's image/title/price/links.
 *    Variant swatches and quick-add are stripped from the clone since they
 *    would still reference the template card's product.
 *
 * Infinite scroll can later append the pinned product's original card
 * (duplicate); a MutationObserver removes the non-pinned duplicate.
 */

const params = new URLSearchParams(window.location.search);
const pinnedHandle = params.get('first');

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'USD';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  } catch (e) {
    return '$' + (cents / 100).toFixed(2);
  }
}

/**
 * Removes any later-loaded (infinite scroll) copy of the pinned product so it
 * never appears twice in the grid.
 * @param {HTMLElement} grid
 * @param {string} productId
 */
function watchForDuplicates(grid, productId) {
  if (!productId) return;
  new MutationObserver(() => {
    for (const li of grid.querySelectorAll(`li.product-grid__item[data-product-id="${productId}"]`)) {
      if (!li.hasAttribute('data-pinned-first')) li.remove();
    }
  }).observe(grid, { childList: true });
}

/**
 * @param {HTMLElement} grid
 * @returns {HTMLLIElement | undefined} the grid item linking to the pinned handle
 */
function findExistingCard(grid) {
  return [...grid.querySelectorAll('li.product-grid__item')].find((li) =>
    li.querySelector(`a[href*="/products/${pinnedHandle}"]`)
  );
}

/**
 * Builds a lookalike card for a product that isn't in the rendered grid, by
 * cloning the first card (so all theme styling carries over) and swapping the
 * product-specific parts.
 * @param {HTMLElement} grid
 * @param {object} product - response of /products/<handle>.js
 */
function buildPinnedCard(grid, product) {
  const template = grid.querySelector('li.product-grid__item');
  if (!template) return null;

  const li = /** @type {HTMLLIElement} */ (template.cloneNode(true));
  li.setAttribute('data-pinned-first', 'true');
  li.dataset.productId = String(product.id);
  li.removeAttribute('id');

  // These would still act on the template card's product - remove them.
  for (const el of li.querySelectorAll(
    'swatches-variant-picker-component, .quick-add, slideshow-arrows, slideshow-controls, .product-card__color-label'
  )) {
    el.remove();
  }

  // Keep only the first media slide so the hover image swap can't show the
  // template product.
  li.querySelectorAll('slideshow-slide').forEach((slide, index) => {
    if (index > 0) slide.remove();
  });

  for (const a of li.querySelectorAll('a')) a.href = product.url;

  const card = li.querySelector('product-card');
  if (card) card.setAttribute('data-product-id', String(product.id));

  const img = li.querySelector('img');
  if (img && product.featured_image) {
    const base = product.featured_image;
    img.src = base + (base.includes('?') ? '&' : '?') + 'width=800';
    img.removeAttribute('srcset');
    img.alt = product.title;
  }

  const titleEl = [...li.querySelectorAll('.text-block p, .text-block')].find(
    (el) => el.textContent.trim().length > 0
  );
  if (titleEl) titleEl.textContent = product.title;

  const priceEl = li.querySelector('product-price');
  if (priceEl) {
    const parts = [formatMoney(product.price)];
    if (product.compare_at_price && product.compare_at_price > product.price) {
      parts.push(formatMoney(product.compare_at_price));
    }
    priceEl.textContent = parts.join(' ');
  }

  return li;
}

function init() {
  const anyItem = document.querySelector('li.product-grid__item');
  const grid = anyItem?.parentElement;
  if (!grid) return;

  const existing = findExistingCard(grid);
  if (existing) {
    existing.setAttribute('data-pinned-first', 'true');
    grid.prepend(existing);
    watchForDuplicates(grid, existing.dataset.productId || '');
    return;
  }

  fetch(`/products/${encodeURIComponent(pinnedHandle)}.js`)
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error('product not found'))))
    .then((product) => {
      const li = buildPinnedCard(grid, product);
      if (!li) return;
      grid.prepend(li);
      watchForDuplicates(grid, String(product.id));
    })
    .catch(() => {
      // Unknown/unavailable handle: leave the grid untouched.
    });
}

if (pinnedHandle) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
